import React, { createContext, useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  buildBlogStructuredData,
  DEFAULT_OG_IMAGE,
  DEFAULT_ROBOTS,
  PRODUCT_POSITIONING,
  normalizeTitle,
  resolveOgImage,
  SITE_NAME,
  SITE_URL,
  toAbsoluteUrl,
  type SEOData,
} from '../seo/metadata'
import { blogPosts } from '../website/data/blogPosts'
import { trackPageView } from '../utils/analytics'

interface SEOCollectorContextValue {
  setMetadata: (metadata: SEOData) => void
}

const SEOCollectorContext = createContext<SEOCollectorContextValue | null>(null)

export const SEOCollectorProvider: React.FC<{
  children: React.ReactNode
  onMetadata: (metadata: SEOData) => void
}> = ({ children, onMetadata }) => (
  <SEOCollectorContext.Provider value={{ setMetadata: onMetadata }}>
    {children}
  </SEOCollectorContext.Provider>
)

const getCybersecurityCategory = (page: string): string => {
  if (page.includes('identity') || page.includes('ad') || page.includes('cloud')) return 'identity-and-cloud-security'
  if (page.includes('reverse') || page.includes('ghidra')) return 'reverse-engineering'
  if (page.includes('report') || page.includes('compliance') || page.includes('evidence')) return 'security-reporting'
  if (page.includes('tool') || page.includes('scan') || page.includes('recon')) return 'assessment-workflows'
  if (page.includes('blog') || page.includes('guide')) return 'security-research'
  return 'cybersecurity-ide'
}

const buildFallbackStructuredData = (
  pathname: string,
  metadata: SEOData,
): Record<string, unknown> | Array<Record<string, unknown>> => {
  // /blog/:slug routes get automatic BlogPosting + BreadcrumbList from the
  // shared registry, so no per-post .tsx edits are needed.
  const blogSchema = buildBlogStructuredData(pathname, blogPosts)
  if (blogSchema) return blogSchema

  const pageType = pathname.includes('/blog/')
    ? 'Article'
    : pathname.includes('/faq')
      ? 'FAQPage'
      : pathname.includes('/cli') || pathname.includes('/product') || pathname.includes('/tools/')
        ? 'SoftwareApplication'
        : 'WebPage'

  const title = normalizeTitle(metadata.title) || SITE_NAME

  const baseData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': pageType,
    name: title,
    description: metadata.description || PRODUCT_POSITIONING,
    url: metadata.canonicalUrl || `${SITE_URL}${pathname === '/' ? '/' : pathname}`,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    category: getCybersecurityCategory(pathname),
  }

  if (pageType === 'SoftwareApplication') {
    baseData.applicationCategory = 'SecurityApplication'
    baseData.operatingSystem = 'Linux, macOS, WSL'
  }

  return baseData
}

const ensureMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value)
  })
}

const ensureLinkTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.querySelector(selector)
  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value)
  })
}

const removeExistingStructuredData = () => {
  document.querySelectorAll('script[data-page-schema]').forEach(script => script.remove())
}

const addStructuredData = (payload: SEOData['structuredData'] | undefined, pathname: string, metadata: SEOData) => {
  const entries = Array.isArray(payload)
    ? payload
    : payload
      ? [payload]
      : [buildFallbackStructuredData(pathname, metadata)]

  removeExistingStructuredData()

  entries.forEach((entry, index) => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-page-schema', `true-${index}`)
    script.textContent = JSON.stringify(entry)
    document.head.appendChild(script)
  })
}

const SEOOptimizer: React.FC<SEOData> = (props) => {
  const location = useLocation()
  const collector = useContext(SEOCollectorContext)

  if (collector) {
    collector.setMetadata(props)
  }

  useEffect(() => {
    const resolvedTitle = normalizeTitle(props.title)
    const resolvedCanonical = toAbsoluteUrl(props.canonicalUrl)
    // resolveOgImage swaps any .svg (invalid for OG cards) for the raster default.
    const resolvedOgImage = toAbsoluteUrl(resolveOgImage(props.openGraph?.image || props.ogImage || DEFAULT_OG_IMAGE))
    const resolvedStructuredData = props.structuredData || buildFallbackStructuredData(location.pathname, {
      ...props,
      canonicalUrl: resolvedCanonical,
      ogImage: resolvedOgImage,
    })

    if (resolvedTitle) {
      document.title = resolvedTitle
    }

    if (props.description) {
      ensureMetaTag('meta[name="description"]', { name: 'description', content: props.description })
    }

    if (props.keywords) {
      ensureMetaTag('meta[name="keywords"]', { name: 'keywords', content: props.keywords })
    }

    ensureMetaTag('meta[name="robots"]', {
      name: 'robots',
      content: props.robots || DEFAULT_ROBOTS,
    })

    if (resolvedCanonical) {
      ensureLinkTag('link[rel="canonical"]', { rel: 'canonical', href: resolvedCanonical })
    }

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(tag => tag.remove())
    props.hreflangTags?.forEach(({ href, hreflang }) => {
      const link = document.createElement('link')
      link.setAttribute('rel', 'alternate')
      link.setAttribute('hreflang', hreflang)
      link.setAttribute('href', toAbsoluteUrl(href) || href)
      document.head.appendChild(link)
    })

    const openGraph = {
      title: resolvedTitle,
      description: props.openGraph?.description || props.description,
      image: resolvedOgImage,
      url: toAbsoluteUrl(props.openGraph?.url || resolvedCanonical),
      type: props.openGraph?.type || (location.pathname.startsWith('/blog/') ? 'article' : 'website'),
    }

    if (openGraph.title) ensureMetaTag('meta[property="og:title"]', { property: 'og:title', content: openGraph.title })
    if (openGraph.description) ensureMetaTag('meta[property="og:description"]', { property: 'og:description', content: openGraph.description })
    if (openGraph.image) ensureMetaTag('meta[property="og:image"]', { property: 'og:image', content: openGraph.image })
    if (openGraph.url) ensureMetaTag('meta[property="og:url"]', { property: 'og:url', content: openGraph.url })
    ensureMetaTag('meta[property="og:type"]', { property: 'og:type', content: openGraph.type })
    ensureMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME })

    const twitter = {
      card: props.twitter?.card || 'summary_large_image',
      title: props.twitter?.title || resolvedTitle,
      description: props.twitter?.description || props.description,
      image: toAbsoluteUrl(props.twitter?.image || resolvedOgImage),
      creator: props.twitter?.creator || '@zypheronsec',
    }

    ensureMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: twitter.card })
    if (twitter.title) ensureMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: twitter.title })
    if (twitter.description) ensureMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: twitter.description })
    if (twitter.image) ensureMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: twitter.image })
    if (twitter.creator) ensureMetaTag('meta[name="twitter:creator"]', { name: 'twitter:creator', content: twitter.creator })

    addStructuredData(resolvedStructuredData, location.pathname, {
      ...props,
      canonicalUrl: resolvedCanonical,
      ogImage: resolvedOgImage,
      structuredData: resolvedStructuredData,
    })

    trackPageView(location.pathname + location.search)
  }, [location, props])

  return null
}

export const seoConfigs = {
  home: {
    title: 'ZYPHERON Desktop | Cybersecurity IDE for Teams and Pentesting Firms',
    description: 'ZYPHERON Desktop is a cybersecurity IDE for security teams and pentesting firms: AI-assisted workflows, local evidence, CLI orchestration, and reports.',
    keywords: 'zypheron desktop, cybersecurity ide, ai security ide, security team workflow, pentest firm workflow, pentest reporting, evidence collection, local-first security tooling, assessment automation, zypheron cli',
    canonicalUrl: `${SITE_URL}/`,
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ZYPHERON Desktop',
          alternateName: ['ZYPHERON', 'ZYPHERON CLI'],
          applicationCategory: 'SecurityApplication',
          operatingSystem: ['Linux', 'macOS', 'WSL'],
          description: PRODUCT_POSITIONING,
          url: SITE_URL,
          keywords: 'cybersecurity ide, ai security ide, security assessment workflow, pentest reporting, evidence collection, local-first security tooling, zypheron desktop',
          featureList: [
            'Unified desktop workspace',
            'Terminal-first CLI companion',
            'AD, identity, and cloud recon',
            'Reverse engineering workspace',
            'Local-LLM and air-gapped operation',
            'Human-in-the-loop assessment workflows',
            'Board and client-ready reporting',
          ],
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is ZYPHERON Desktop?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'ZYPHERON Desktop is a cybersecurity IDE that combines AI-assisted recon, terminal execution, reverse engineering, evidence collection, and reporting in one workspace for security teams and pentesting firms.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is the CLI still available?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. The open source ZYPHERON CLI remains available as a terminal-first path for teams and firms that want local workflows, repo-backed installs, and practical automation around recon and scanning.',
              },
            },
            {
              '@type': 'Question',
              name: 'How is ZYPHERON different from other security tools?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'ZYPHERON ties workspace context to terminal execution, recon, reverse engineering, AI assistance, evidence collection, and reporting so findings stay connected instead of being scattered across separate tools.',
              },
            },
          ],
        },
      ],
  },
  login: {
    title: 'Login to ZYPHERON',
    description: 'Access your ZYPHERON account for desktop licensing, billing, and managed product features.',
    keywords: 'zypheron login, zypheron account',
    canonicalUrl: `${SITE_URL}/login`,
    ogImage: DEFAULT_OG_IMAGE,
    robots: 'noindex, nofollow',
  },
  billing: {
    title: 'ZYPHERON Pricing',
    description: 'Explore ZYPHERON Desktop pricing, the open source CLI, and future enterprise options.',
    keywords: 'zypheron pricing, cybersecurity ide pricing, zypheron cli',
    canonicalUrl: `${SITE_URL}/billing`,
    ogImage: DEFAULT_OG_IMAGE,
    robots: 'noindex, nofollow',
  },
  freeTrial: {
    title: 'Download ZYPHERON Desktop',
    description: 'Download ZYPHERON Desktop, the cybersecurity IDE for AI-assisted assessments, local evidence collection, and board/client-ready reports.',
    keywords: 'download zypheron desktop, cybersecurity ide download, ai security ide download, pentest workflow, security assessment workflow, zypheron cli',
    canonicalUrl: `${SITE_URL}/download`,
    ogImage: DEFAULT_OG_IMAGE,
  },
  cli: {
    title: 'ZYPHERON CLI | Open Source Recon and Assessment Automation',
    description: 'Open source ZYPHERON CLI for terminal-first recon, nmap automation, nuclei automation, AI-assisted assessment workflows, and local security tooling.',
    keywords: 'zypheron cli, assessment automation cli, nmap automation, nuclei automation, open source security cli, local security tooling',
    canonicalUrl: `${SITE_URL}/cli`,
    ogImage: DEFAULT_OG_IMAGE,
  },
  cloud: {
    title: 'ZYPHERON Cloud | Shared Reporting & Risk Visibility',
    description: 'ZYPHERON Cloud is the browser layer for shared reporting, risk and compliance visibility, and executive or client dashboards built on top of your desktop assessments.',
    keywords: 'zypheron cloud, cybersecurity reporting dashboard, pentest reporting platform, executive security dashboard, compliance reporting platform, client reporting',
    canonicalUrl: `${SITE_URL}/cloud`,
    ogImage: DEFAULT_OG_IMAGE,
  },
}

export type SEOOptimizerProps = SEOData
export default SEOOptimizer
